import { getStaffSessionToken } from '../auth/staffSession';
import React, { useState } from 'react';
import brand from '../../config/brand';
import { api, preview } from './GroupActivation';

type TeamOrder = {
  id: string;
  email: string;
  status: string;
  customer_id?: string;
  subscription_id?: string;
  renewal_at?: number;
  items: { iccid: string; nickname: string; state: string; error?: string }[];
  plan?: { name: string; unit_amount: number; standard_amount: number; interval: string; currency?: string; region?: string };
  provider_error?: string;
  management?: { action: string; state: string; effective_at: number };
};

export const GroupActivationTeam = () => {
  const staffSession = getStaffSessionToken();
  const [orders, setOrders] = useState<TeamOrder[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<TeamOrder | null>(null);
  const [action, setAction] = useState('retry');
  const [planId, setPlanId] = useState('light_year');
  const [iccid, setIccid] = useState('');
  const [resume, setResume] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const formatMoney = (minorUnits: number, currency = 'gbp') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(minorUnits / 100);

  async function load() {
    setBusy(true);
    setError('');
    try {
      setOrders((await api('/team/orders', 'POST', {})).orders);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!selected || !confirmed) return;
    setBusy(true);
    setError('');
    const recovery = ['retry', 'refund-failed'].includes(action);
    try {
      await api(`/team/orders/${selected.id}/${recovery ? action : 'manage'}`, 'POST', {
        action,
        plan_id: planId,
        iccid,
        resume_date: resume,
        standard_price_acknowledged: confirmed
      });
      setSelected(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ga">
      {preview && (
        <div className="ga-preview">
          <strong>LOCAL PREVIEW</strong> Team actions affect simulated SIMs and payments only.
        </div>
      )}
      <header className="ga-header">
        <img src={brand.logo} alt={brand.displayName} />
        <a href="/activate/group">Customer activation ↗</a>
      </header>
      <main className="ga-main">
        <div className="ga-intro">
          <span className="ga-eyebrow">TEAM TOOLS</span>
          <h1>Group activations</h1>
          <p>Review progress, recover failed activations and schedule group changes.</p>
        </div>
        <section className="ga-panel">
          {!preview && !staffSession && <p><a href="/team-login?next=/team/group-activations">Sign in with Google to view group activations</a></p>}
          <button className="ga-button" disabled={busy || (!preview && !staffSession)} onClick={load}>
            Refresh activations
          </button>
          {error && (
            <p className="ga-error" role="alert">
              {error}
            </p>
          )}
          {orders.map((order) => (
            <article className="ga-sim" style={{ marginTop: 20 }} key={order.id}>
              <div className="ga-sim-top">
                <div>
                  <strong>{order.email}</strong>
                  <p>
                    {order.status.replace(/_/g, ' ')} · {order.items.length} SIMs ·{' '}
                    {order.plan?.name}
                  </p>
                </div>
              </div>
              <p className="ga-hint">
                {order.id}
                <br />
                {order.subscription_id}
              </p>
              {order.renewal_at && (
                <p className="ga-note">
                  Renewal: {new Date(order.renewal_at * 1000).toLocaleDateString('en-GB')}
                </p>
              )}
              {order.items.map((item) => (
                <p className="ga-note" key={item.iccid}>
                  <strong>
                    {item.nickname || 'SIM'} · {item.iccid.slice(-4)}
                  </strong>{' '}
                  — {item.state}
                  {item.error && (
                    <span className="ga-error" style={{ display: 'block' }}>
                      {item.error}
                    </span>
                  )}
                </p>
              ))}
              {order.provider_error && <p className="ga-error">{order.provider_error}</p>}
              {order.management && (
                <p className="ga-note">
                  {order.management.action}: {order.management.state} ·{' '}
                  {new Date(order.management.effective_at * 1000).toLocaleDateString('en-GB')}
                </p>
              )}
              <button
                className="ga-back"
                onClick={() => {
                  setSelected(order);
                  setAction(order.status === 'needs_attention' ? 'retry' : 'change_plan');
                  setPlanId(`${order.plan?.region === 'ie' ? 'ie_' : ''}${(order.plan?.name || 'light').toLowerCase()}_${order.plan?.interval || 'year'}`);
                  setConfirmed(false);
                  setIccid(order.items.find((i) => i.state === 'active')?.iccid || '');
                }}
              >
                Review team action →
              </button>
            </article>
          ))}
          {selected && (
            <section className="ga-confirm" aria-label="Review team action">
              <h2>Review action for {selected.email}</h2>
              <p className="ga-hint">
                All changes target subscription {selected.subscription_id || 'not yet created'}.
              </p>
              <label className="ga-label" htmlFor="group-action">
                Action
              </label>
              <select
                id="group-action"
                className="ga-input"
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  setConfirmed(false);
                }}
              >
                <option value="retry">Retry / reconcile activation</option>
                <option value="refund-failed">Refund confirmed unfulfilled SIMs</option>
                <option value="change_plan">Change whole-group plan at renewal</option>
                <option value="pause">Pause monthly group at renewal</option>
                <option value="remove">Remove one SIM at renewal</option>
                <option value="cancel">Cancel whole group at renewal</option>
              </select>
              {action === 'change_plan' && (
                <>
                  <label className="ga-label" htmlFor="group-new-plan">
                    New plan
                  </label>
                  <select
                    className="ga-input"
                    id="group-new-plan"
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                  >
                    {['light', 'standard', 'heavy'].flatMap((name) =>
                      ['year', 'month'].map((interval) => (
                        <option key={`${name}_${interval}`} value={`${selected.plan?.region === 'ie' ? 'ie_' : ''}${name}_${interval}`}>
                          {name} · {interval}
                        </option>
                      ))
                    )}
                  </select>
                </>
              )}
              {action === 'pause' && (
                <>
                  <label className="ga-label" htmlFor="group-resume">
                    Resume date
                  </label>
                  <input
                    className="ga-input"
                    id="group-resume"
                    type="date"
                    value={resume}
                    onChange={(e) => setResume(e.target.value)}
                  />
                </>
              )}
              {action === 'remove' && (
                <>
                  <label className="ga-label" htmlFor="group-remove">
                    SIM to remove
                  </label>
                  <select
                    className="ga-input"
                    id="group-remove"
                    value={iccid}
                    onChange={(e) => setIccid(e.target.value)}
                  >
                    {selected.items
                      .filter((i) => i.state === 'active')
                      .map((i) => (
                        <option value={i.iccid} key={i.iccid}>
                          {i.nickname} · {i.iccid.slice(-4)}
                        </option>
                      ))}
                  </select>
                </>
              )}
              {['remove', 'refund-failed', 'change_plan'].includes(action) && (
                <p className="ga-note">
                  If one annual SIM remains, standard pricing applies at its next renewal.
                  {selected.plan?.standard_amount
                    ? ` The current plan’s standard price is ${formatMoney(selected.plan.standard_amount, selected.plan.currency)} per year.`
                    : ''}{' '}
                  Disclose the applicable renewal price to the customer before confirming.
                </p>
              )}
              <label className="ga-check ga-terms">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                <span>
                  I have checked the affected SIMs, subscription and customer instructions. For a
                  removal or refund leaving one SIM, I have disclosed its standard renewal price.
                  Refunds apply now; plan changes take effect at renewal.
                </span>
              </label>
              <div className="ga-footer">
                <button className="ga-back" onClick={() => setSelected(null)}>
                  Cancel
                </button>
                <button className="ga-button" disabled={!confirmed || busy} onClick={submit}>
                  {preview ? 'Apply test action' : 'Apply team action'}
                </button>
              </div>
            </section>
          )}
        </section>
      </main>
    </div>
  );
};
